import "./ErrorLogs.css";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import useApi from "../../../helpers/apiHelper/requestHelper";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = dayjs(value);
  return date.isValid() ? date.format("DD MMM YYYY, HH:mm:ss") : "—";
};

const titleCase = (value) => {
  if (value == null || value === "") return "—";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const severityClass = (severity) => {
  const key = String(severity || "").toLowerCase();
  if (key === "critical" || key === "error") return "is-critical";
  if (key === "warning") return "is-warning";
  return "is-info";
};

const DetailRow = ({ label, value }) => (
  <div className="errorLogDetailRow">
    <span className="errorLogDetailLabel">{label}</span>
    <span className="errorLogDetailValue">{value ?? "—"}</span>
  </div>
);

const ErrorLogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  const [log, setLog] = useState(location.state?.log || null);
  const [loading, setLoading] = useState(!location.state?.log);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const fetchDetail = async () => {
      if (!id) return;
      setLoading(true);
      setError("");
      try {
        const response = await api(
          "get",
          `authorizeSuperAdmin/error-logs/${id}`
        );
        if (cancelled) return;
        if (response?.status) {
          const payload = response.data?.item || response.data || null;
          if (payload && (payload._id || payload.message)) {
            setLog(payload);
          } else if (!log) {
            setError("Error log not found");
          }
        } else if (!log) {
          setError(response?.message || "Failed to load error log");
        }
      } catch (err) {
        if (!cancelled && !log) {
          setError("Failed to load error log");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDetail();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading && !log) {
    return (
      <div className="errorLogsPage">
        <p className="errorLogsEmptyText">Loading error log…</p>
      </div>
    );
  }

  if (!log) {
    return (
      <div className="errorLogsPage">
        <button
          type="button"
          className="errorLogsBackBtn"
          onClick={() => navigate("/authorizeSuperAdmin/error-logs")}
        >
          ← Back to logs
        </button>
        <p className="errorLogsEmptyText">{error || "Error log not found"}</p>
      </div>
    );
  }

  const details = log.details && typeof log.details === "object" ? log.details : {};

  return (
    <div className="errorLogsPage">
      <div className="errorLogsHeader">
        <div>
          <button
            type="button"
            className="errorLogsBackBtn"
            onClick={() => navigate("/authorizeSuperAdmin/error-logs")}
          >
            ← Back to logs
          </button>
          <h2 className="errorLogsTitle">Error Log Detail</h2>
          <p className="errorLogsSubtitle">{log._id}</p>
        </div>
        <span className={`errorLogsBadge ${severityClass(log.severity)}`}>
          {titleCase(log.severity)}
        </span>
      </div>

      <div className="errorLogDetailCard">
        <h3 className="errorLogDetailHeading">{log.message || "—"}</h3>
        <div className="errorLogDetailGrid">
          <DetailRow label="Status" value={titleCase(log.status)} />
          <DetailRow label="Module" value={titleCase(log.module)} />
          <DetailRow label="Action" value={titleCase(log.action)} />
          <DetailRow label="Actor role" value={titleCase(log.actorRole)} />
          <DetailRow label="Origin" value={titleCase(log.origin)} />
          <DetailRow label="Source" value={log.source || "—"} />
          <DetailRow label="Environment" value={titleCase(log.environment)} />
          <DetailRow
            label="Demo trading"
            value={log.demoTrading ? "Yes" : "No"}
          />
          <DetailRow label="Exchange" value={titleCase(log.exchange)} />
          <DetailRow label="Market" value={titleCase(log.marketType)} />
          <DetailRow label="Symbol" value={log.symbol || "—"} />
          <DetailRow label="Leader order ID" value={log.leaderOrderId || "—"} />
          <DetailRow label="Correlation ID" value={log.correlationId || "—"} />
          <DetailRow
            label="Occurrence count"
            value={log.occurrenceCount ?? 1}
          />
          <DetailRow
            label="First occurred"
            value={formatDateTime(log.firstOccurredAt)}
          />
          <DetailRow
            label="Last occurred"
            value={formatDateTime(log.lastOccurredAt)}
          />
          <DetailRow label="Created" value={formatDateTime(log.createdAt)} />
          <DetailRow label="Updated" value={formatDateTime(log.updatedAt)} />
        </div>
      </div>

      <div className="errorLogDetailColumns">
        <div className="errorLogDetailCard">
          <h4 className="errorLogDetailHeading">Member</h4>
          <div className="errorLogDetailGrid">
            <DetailRow
              label="Email"
              value={log.userEmail || log.user?.email || "—"}
            />
            <DetailRow label="User ID" value={log.user?._id || "—"} />
            <DetailRow label="Role" value={titleCase(log.user?.role)} />
          </div>
        </div>

        <div className="errorLogDetailCard">
          <h4 className="errorLogDetailHeading">Leader</h4>
          <div className="errorLogDetailGrid">
            <DetailRow label="Email" value={log.leader?.email || "—"} />
            <DetailRow label="Leader ID" value={log.leader?._id || "—"} />
            <DetailRow label="Role" value={titleCase(log.leader?.role)} />
          </div>
        </div>
      </div>

      <div className="errorLogDetailCard">
        <h4 className="errorLogDetailHeading">Details</h4>
        {Object.keys(details).length === 0 ? (
          <p className="errorLogsEmptyText">No extra details</p>
        ) : (
          <div className="errorLogDetailGrid">
            {Object.entries(details).map(([key, value]) => (
              <DetailRow
                key={key}
                label={titleCase(key)}
                value={
                  value == null || value === ""
                    ? "—"
                    : typeof value === "object"
                      ? JSON.stringify(value)
                      : String(value)
                }
              />
            ))}
          </div>
        )}
      </div>

      <div className="errorLogDetailCard">
        <h4 className="errorLogDetailHeading">Raw JSON</h4>
        <pre className="errorLogRawJson">{JSON.stringify(log, null, 2)}</pre>
      </div>
    </div>
  );
};

export default ErrorLogDetail;
